import React from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Box, Tooltip } from "@mui/material";

import { Avatar, TableCell } from "@shared/components";
import { useDateOptions } from "@shared/options";
import { ReceivedBook } from "@modules/receivedBooks/types";

const FALLBACK_COVER = "/images/image-icon-picture-not-available-picturer.jpeg";

export const useReceivedBookListColumns = (): GridColDef<ReceivedBook>[] => {
  const { getMonthOptions } = useDateOptions();
  const monthOptions = getMonthOptions();

  return React.useMemo<GridColDef<ReceivedBook>[]>(() => [
    {
      field: "id",
      headerName: "ID",
      width: 80,
    },
    {
      field: "hash_code",
      headerName: "Hash do Log",
      minWidth: 280,
      sortable: false,
      renderCell: ({ value }) => (
        <TableCell.Text value={value || "-"} />
      ),
    },
    {
      field: "document",
      headerName: "Documento",
      minWidth: 140,
      renderCell: ({ value }) => (
        <TableCell.Text value={value || "-"} />
      ),
    },
    {
      field: "display_type",
      headerName: "Tipo",
      width: 110,
      sortable: false,
      headerAlign: "center",
      renderCell: ({ row }) => (
        <TableCell.Text value={row.book?.display_type || "-"} align="center" />
      ),
    },
    {
      field: "isbn",
      headerName: "ISBN",
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <TableCell.Text value={row.book?.isbn || "-"} />
      ),
    },
    {
      field: "issn",
      headerName: "ISSN",
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <TableCell.Text value={row.book?.issn || "-"} />
      ),
    },
    {
      field: "cover",
      headerName: "Capa",
      width: 80,
      sortable: false,
      headerAlign: "center",
      renderCell: ({ row }) => {
        const imageSrc = row.book?.cover_optimized || row.book?.cover || FALLBACK_COVER;

        return (
          <TableCell.Container align="center">
            <Tooltip
              arrow
              placement="right"
              slotProps={{
                tooltip: { sx: { p: 0, maxWidth: "none" } },
              }}
              title={
                <Box
                  component="img"
                  src={imageSrc}
                  alt={row.book?.title ?? "Book cover"}
                  sx={{ maxWidth: 320, maxHeight: 420, display: "block" }}
                />
              }
            >
              <Box sx={{ width: 48 }}>
                <Avatar
                  src={imageSrc}
                  alt={row.book?.title ?? "Book cover"}
                  shape="square"
                  fullWidth
                />
              </Box>
            </Tooltip>
          </TableCell.Container>
        );
      },
    },
    {
      field: "book",
      headerName: "Livro",
      minWidth: 260,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <TableCell.Link
          to={`/books/edit/${row.book?.id}`}
          value={row.book?.title ?? "-"}
          direction="column"
          gap={0}
        />
      ),
    },
    {
      field: "author",
      headerName: "Autor",
      minWidth: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <TableCell.Text value={row.book?.author || "-"} />
      ),
    },
    {
      field: "publisher",
      headerName: "Editora",
      minWidth: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <TableCell.Text value={row.book?.publisher?.name || "-"} />
      ),
    },
    {
      field: "agreement",
      headerName: "Convênio",
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <TableCell.Link
          to={row.agreement?.id ? `/agreement/edit/${row.agreement.id}` : "#"}
          value={row.agreement?.name ?? "-"}
        />
      ),
    },
    {
      field: "user",
      headerName: "Usuário",
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <TableCell.Link
          to={row.user?.id ? `/users/edit/${row.user.id}` : "#"}
          value={row.user?.name ? `${row.user.name} - ${row.user?.email ?? "N/A"}` : "N/A"}
        />
      ),
    },
    {
      field: "month_reference",
      headerName: "Mês/Ano Ref.",
      minWidth: 140,
      headerAlign: "center",
      renderCell: ({ row }) => {
        const monthLabel = monthOptions.find(m => m.value === row.month_reference)?.label;
        return (
          <TableCell.Text
            value={monthLabel ? `${monthLabel}/${row.year_reference}` : "-"}
            align="center"
          />
        );
      },
    },
    {
      field: "has_sent_email_notification",
      headerName: "E-mail Enviado",
      width: 140,
      headerAlign: "center",
      sortable: false,
      renderCell: ({ value }) => (
        <TableCell.Text value={value ? "Sim" : "Não"} align="center" />
      ),
    },
    {
      field: "sent_email_at",
      headerName: "Data Envio E-mail",
      minWidth: 160,
      headerAlign: "center",
      renderCell: ({ value }) => (
        <TableCell.Date value={value} align="center" />
      ),
    },
    {
      field: "created_at",
      headerName: "Recebido em",
      minWidth: 160,
      headerAlign: "center",
      renderCell: ({ value }) => (
        <TableCell.Date value={value} align="center" />
      ),
    },
  ], [monthOptions]);
};
